import cx from 'classnames'
import dynamic from 'next/dynamic'
import { useContext } from 'react'

import {
  type SanityBlogPostWithoutBody,
  type SanityBlogPostOptions,
} from '@data/sanity/queries/types/blog'
import { getFormattedDate } from '@lib/format'
import { LanguageContext } from '@lib/language'
import { getLinkPageUrl } from '@lib/routes'
import { StringsContext } from '@lib/strings'

import ArrowRightIcon from '@components/icons/arrow-right'
import SimpleLink from '@components/simple-link'

const Photo = dynamic(() => import('@components/photo'))

type VerticalAlign = 'top' | 'center'

interface BlogPostListItemProps {
  post: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
  verticalAlign?: VerticalAlign
}

const BlogPostListItem = ({
  post,
  options,
  verticalAlign,
}: BlogPostListItemProps) => {
  const strings = useContext(StringsContext)
  const { locale } = useContext(LanguageContext)

  return (
    <div
      className={cx('group grid grid-cols-1 lg:grid-cols-2', {
        'items-start': !verticalAlign || verticalAlign === 'top',
        'items-center': verticalAlign === 'center',
      })}
    >
      {!!post.coverImage && !!options?.showCoverImage && (
        <SimpleLink
          href={getLinkPageUrl('blogPost', post.slug)}
          className="overflow-hidden rounded-lg mb-5"
        >
          <Photo
            className="group-hover:scale-105 transition-transform duration-500"
            image={{
              ...post.coverImage,
              customRatio: 1.7777777778,
            }}
          />
        </SimpleLink>
      )}

      <div className="lg:px-10 xl:px-28 flex flex-col gap-1">
        {!!options?.showCategories && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2">
            {post.categories?.map((category) => (
              <SimpleLink
                key={`${post.slug}-category-${category.slug}`}
                href={getLinkPageUrl('blogCategory', category.slug)}
                className="text-[12px] uppercase hover:underline"
              >
                {category.name}
              </SimpleLink>
            ))}
          </div>
        )}

        <SimpleLink href={getLinkPageUrl('blogPost', post.slug)}>
          <h3 className="mb-4 hover:underline">{post.title}</h3>
        </SimpleLink>

        {!!options?.showExcerpt && <p className="mb-6">{post.excerpt}</p>}

        {(!!options?.showAuthorPhoto ||
          !!options?.showAuthorName ||
          !!options?.showDate) && (
          <div className="flex items-center mb-5">
            {options?.showAuthorPhoto && post.author?.photo && (
              <Photo
                image={post.author?.photo}
                width={40}
                height={40}
                className="rounded-full overflow-hidden shrink-0 w-[40px] h-[40px] mr-2"
                imageClassName="object-cover"
              />
            )}
            <div>
              {options?.showAuthorName && (
                <p className="text-sm">
                  {strings.blogPostBy}{' '}
                  <SimpleLink
                    href={getLinkPageUrl('blogAuthor', post.author?.slug)}
                    className="hover:underline"
                  >
                    {post.author?.name}
                  </SimpleLink>
                  {options?.showDate
                    ? `, ${getFormattedDate(post.date, locale)}`
                    : ''}
                </p>
              )}
            </div>
          </div>
        )}

        <SimpleLink
          href={getLinkPageUrl('blogPost', post.slug)}
          className="group/read-more uppercase inline-flex items-center font-medium text-sm hover:underline"
        >
          <div className="group-hover/read-more:bg-yellow rounded-full mr-1 p-1">
            <ArrowRightIcon id="read-more-icon" className="text-xl" />
          </div>
          {strings.blogPostReadMore}
        </SimpleLink>
      </div>
    </div>
  )
}

export default BlogPostListItem
